import React, { useContext } from "react";

import { ApiRequest } from "api/makeApiRequest/ApiRequest";
import { AlertContext } from "components/AlertProvider/AlertProvider";
import ModalConfirm from "components/ModalConfirm";
import { Method } from "hooks/useSendData";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import ResponseForm from "components/ResponseForm/ResponseForm";

import { Contents } from "./ModalRespond.styles";

export type Props<T extends { id: string }> = {
	isOpen: boolean;
	onCancel: () => void;
	OKButtonIsDisabled?: boolean;
	currentResponse?: boolean;
	setCurrentResponse: React.Dispatch<React.SetStateAction<boolean | undefined>>;
	sendResponse: Method<{ id: string; response: boolean; message?: string }, {}>;
	respondingTo?: T;
	responseApiRequest: React.MutableRefObject<ApiRequest<unknown> | undefined>;
	responseNote?: string;
	setResponseNote: React.Dispatch<React.SetStateAction<string | undefined>>;
	additionalValidation?: (respondingTo: T) => void;
	additionalDataToSubmit?: (
		respondingTo: T,
		currentResponse: boolean
	) => Record<string, unknown>;
	onResponseSent: React.Dispatch<React.SetStateAction<number | undefined>>;
	summary: (respondingTo: T, currentUid: string) => React.ReactNode;
	heading: string;
	extraContent?: React.ReactNode;
};

function ModalRespond<T extends { id: string }>({
	isOpen,
	onCancel,
	OKButtonIsDisabled = false,
	currentResponse,
	setCurrentResponse,
	sendResponse,
	respondingTo,
	responseApiRequest,
	responseNote,
	setResponseNote,
	additionalValidation,
	additionalDataToSubmit,
	onResponseSent,
	summary,
	heading,
	extraContent
}: Props<T>) {
	const [addAlert] = useContext(AlertContext);
	const [{ uid }] = useContext(AuthContext);

	return (
		<ModalConfirm
			isOpen={!!isOpen}
			title="Invitation"
			onCancel={onCancel}
			OKButtonIsDisabled={OKButtonIsDisabled}
			onOK={async () => {
				if (currentResponse === undefined) {
					addAlert({ contents: "Please indicate 'yes' or 'no'" });
					return;
				}

				if (!respondingTo) {
					throw new Error("Nothing to accept or reject");
				}

				if (additionalValidation) {
					additionalValidation(respondingTo);
				}

				const requestData = {
					id: respondingTo.id,
					response: currentResponse,
					message: responseNote,
					...(additionalDataToSubmit
						? additionalDataToSubmit(respondingTo, currentResponse)
						: {})
				};

				responseApiRequest.current = sendResponse(requestData, { uid });

				onResponseSent(new Date().getTime());
			}}
			okText="Send response"
		>
			{respondingTo && uid
				? (() => {
						return (
							<Contents>
								{summary(respondingTo, uid)}

								<h3>{heading}</h3>

								<ResponseForm
									currentResponse={currentResponse}
									setResponse={setCurrentResponse}
									currentNote={responseNote}
									setNote={setResponseNote}
									extraContent={extraContent}
								/>
							</Contents>
						);
				  })()
				: null}
		</ModalConfirm>
	);
}

export default ModalRespond;
